/**
 * Renders Markdown as React elements — never as an HTML string. The parsing and the rules for
 * where a link may go live in lib/markdown.ts; see there for why.
 */

import { Fragment, useMemo, type MouseEvent, type ReactNode } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import { parseMarkdown, targetOf, type Block, type Inline } from '../lib/markdown'

interface RenderContext {
  path?: string
  onOpenDoc?: (path: string, anchor?: string) => void
  prefix: string
}

export function Markdown({
  source,
  path,
  onOpenDoc,
  idPrefix = 'doc-',
}: {
  source: string
  /** The document's own path, which relative links are resolved against. */
  path?: string
  onOpenDoc?: (path: string, anchor?: string) => void
  /** Keeps heading ids apart from the application's own. */
  idPrefix?: string
}) {
  const blocks = useMemo(() => parseMarkdown(source), [source])
  return <div className="md">{renderBlocks(blocks, { path, onOpenDoc, prefix: idPrefix })}</div>
}

function renderBlocks(blocks: Block[], context: RenderContext, tight = false): ReactNode {
  return blocks.map((block, index) => <Fragment key={index}>{renderBlock(block, context, tight)}</Fragment>)
}

function renderBlock(block: Block, context: RenderContext, tight: boolean): ReactNode {
  switch (block.t) {
    case 'heading': {
      const Tag = `h${block.level}` as 'h1'
      return <Tag id={context.prefix + block.id}>{renderInlines(block.c, context)}</Tag>
    }
    case 'paragraph':
      return tight ? renderInlines(block.c, context) : <p>{renderInlines(block.c, context)}</p>
    case 'code':
      return (
        <pre className="md__code" data-lang={block.lang || undefined}>
          <code>{block.v}</code>
        </pre>
      )
    case 'quote':
      return <blockquote>{renderBlocks(block.c, context)}</blockquote>
    case 'hr':
      return <hr />
    case 'list': {
      const items = block.items.map((item, index) => (
        <li key={index} className={item.checked === null ? undefined : 'md__task'}>
          {item.checked !== null && <input type="checkbox" checked={item.checked} readOnly disabled aria-hidden />}
          {renderBlocks(item.c, context, block.tight)}
        </li>
      ))
      return block.ordered ? <ol start={block.start === 1 ? undefined : block.start}>{items}</ol> : <ul>{items}</ul>
    }
    case 'table':
      return (
        <div className="md__table">
          <table>
            <thead>
              <tr>
                {block.head.map((cell, column) => (
                  <th key={column} style={block.align[column] ? { textAlign: block.align[column]! } : undefined}>
                    {renderInlines(cell, context)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, column) => (
                    <td key={column} style={block.align[column] ? { textAlign: block.align[column]! } : undefined}>
                      {renderInlines(cell, context)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
  }
}

function renderInlines(nodes: Inline[], context: RenderContext): ReactNode {
  return nodes.map((node, index) => <Fragment key={index}>{renderInline(node, context)}</Fragment>)
}

function renderInline(node: Inline, context: RenderContext): ReactNode {
  switch (node.t) {
    case 'text':
      return node.v
    case 'code':
      return <code>{node.v}</code>
    case 'em':
      return <em>{renderInlines(node.c, context)}</em>
    case 'strong':
      return <strong>{renderInlines(node.c, context)}</strong>
    case 'del':
      return <del>{renderInlines(node.c, context)}</del>
    case 'br':
      return <br />
    case 'image': {
      const target = targetOf(node.src, context.path)
      if (target.kind !== 'external') return node.alt
      return (
        <a className="md__image" href={target.href} target="_blank" rel="noopener noreferrer nofollow" title={target.href}>
          <ImageIcon size={13} aria-hidden />
          {node.alt || hostOf(target.href)}
        </a>
      )
    }
    case 'link':
      return renderLink(node, context)
  }
}

function renderLink(node: Extract<Inline, { t: 'link' }>, context: RenderContext): ReactNode {
  const children = renderInlines(node.c, context)
  const target = targetOf(node.href, context.path)

  switch (target.kind) {
    case 'external':
      return (
        <a href={target.href} title={node.title} target="_blank" rel="noopener noreferrer nofollow">
          {children}
        </a>
      )
    case 'anchor': {
      const id = context.prefix + target.id
      const jump = (event: MouseEvent): void => {
        event.preventDefault()
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      return (
        <a href={`#${id}`} title={node.title} onClick={jump}>
          {children}
        </a>
      )
    }
    case 'doc': {
      const open = (event: MouseEvent): void => {
        if (!context.onOpenDoc) return
        event.preventDefault()
        context.onOpenDoc(target.path, target.anchor)
      }
      return (
        <a href={`/project/docs?path=${encodeURIComponent(target.path)}`} title={node.title ?? target.path} onClick={open}>
          {children}
        </a>
      )
    }
    case 'none':
      return <span className="md__dead-link">{children}</span>
  }
}

function hostOf(href: string): string {
  try {
    return new URL(href).host
  } catch {
    return href
  }
}
